import clsx from 'clsx';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Edit, Trash2, X } from 'react-feather';
import { useForm } from 'react-hook-form';
import Modal from 'react-modal';
import { ExpirationPaymentStatus } from '../enums/expiration-payment-status';
import { CreateExpirationDto } from '../interfaces/create-expiration.dto';
import { DeliveryNote } from '../interfaces/delivery-note';
import { Expiration } from '../interfaces/expiration';
import { ExpirationPaymentDto } from '../interfaces/expiration-payment.dto';
import { Invoice } from '../interfaces/invoice';
import { api } from '../services/api';
import { expirationPaymentStatusToString, formatNumber, toFixedTrunc } from '../services/helpers';
import myToastr from '../services/toastr';

interface ExpirationModalProps {
  closeModal: (expiration: Expiration | null) => void;
  deliveryNote?: DeliveryNote;
  expiration?: Expiration | null;
  invoice?: Invoice;
  show: boolean;
}

interface ErrorsExpirationPayment {
  amount: boolean;
  expirationDate: boolean;
  showAmountErrorInfo: boolean;
}

interface CustomCreateExpirationDto extends CreateExpirationDto {
  delayDays: number;
  numPayments: number;
}

interface CustomExpirationPaymentDto extends ExpirationPaymentDto {
  remittanceInternalId: string | null;
}

const initialExpirationDto: CustomCreateExpirationDto = {
  date: moment().format('YYYY-MM-DD'),
  delayDays: 30,
  deliveryNoteId: -1,
  expirationPayments: [],
  externalNumber: '',
  invoiceId: -1,
  numPayments: 0,
  storeId: -1,
  total: null,
};

const initialExpirationPaymentDto: CustomExpirationPaymentDto = {
  expirationDate: moment().add(30, 'days').format('YYYY-MM-DD'),
  amount: 0,
  status: ExpirationPaymentStatus.Pending,
  remittanceInternalId: null,
};

const ExpirationModal = ({ closeModal, deliveryNote, expiration, invoice, show }: ExpirationModalProps) => {
  const [expirationPaymentsDtos, setExpirationPaymentsDtos] = useState<CustomExpirationPaymentDto[]>([{ ...initialExpirationPaymentDto }]);
  const [indexSelectedExpirationPaymentDto, setIndexSelectedExpirationPaymentDto] = useState<number>(-1);
  const [newExpirationPayment, setNewExpirationPayment] = useState<CustomExpirationPaymentDto>({ ...initialExpirationPaymentDto });
  const [errorsExpirationPayment, setErrorsExpirationPayment] = useState<ErrorsExpirationPayment>({
    expirationDate: false,
    amount: false,
    showAmountErrorInfo: false,
  });
  const {
    register: registerExpiration,
    handleSubmit: handleSubmitExpiration,
    reset: resetExpiration,
    watch: watchExpiration,
    getValues: getValuesExpiration,
    formState: { errors: errorsExpiration },
    setValue: setValueExpiration,
  } = useForm<CustomCreateExpirationDto>({
    mode: 'onSubmit',
    defaultValues: { ...initialExpirationDto },
  });
  const validNewExpirationPayment: boolean = useMemo(() => {
    if (!newExpirationPayment.expirationDate || !moment(newExpirationPayment.expirationDate).isValid()) {
      return false;
    }
    if (newExpirationPayment.amount === null || newExpirationPayment.amount === 0) {
      return false;
    }
    return true;
  }, [newExpirationPayment]);

  useEffect(() => {
    if (show) {
      if (expiration) {
        let delayDays = 0;
        if (expiration.expirationPayments.length > 1) {
          const firstExpirationPayment = expiration.expirationPayments[0];
          const lastExpirationPayment = expiration.expirationPayments[expiration.expirationPayments.length - 1];
          delayDays = Math.abs(moment(lastExpirationPayment.expirationDate).diff(moment(firstExpirationPayment.expirationDate), 'days'));
        }
        resetExpiration({
          externalNumber: expiration.externalNumber,
          date: moment(expiration.date).format('YYYY-MM-DD'),
          storeId: expiration.storeId,
          deliveryNoteId: expiration.deliveryNoteId,
          total: expiration.total,
          numPayments: expiration.expirationPayments.length,
          delayDays,
        });
        const payments: CustomExpirationPaymentDto[] = [];
        expiration.expirationPayments.forEach((expirationPayment) => {
          payments.push({
            id: expirationPayment.id,
            expirationDate: moment(expirationPayment.expirationDate).format('YYYY-MM-DD'),
            amount: expirationPayment.amount,
            status: expirationPayment.status,
            remittanceInternalId: expirationPayment?.remittance?.internalId || null,
          });
        });
        setExpirationPaymentsDtos(payments);
      } else {
        if (deliveryNote) {
          setValueExpiration('externalNumber', deliveryNote.externalNumber);
          setValueExpiration('total', deliveryNote.total);
          setValueExpiration('numPayments', 1);
          setValueExpiration('storeId', deliveryNote.storeId);
        } else if (invoice) {
          setValueExpiration('externalNumber', invoice.internalId);
          setValueExpiration('total', invoice.total);
          setValueExpiration('storeId', invoice.storeId);
          setValueExpiration('numPayments', invoice.customer?.installmentCount || invoice.organizationClient?.installmentCount || 1);
          setValueExpiration('delayDays', invoice.customer?.installmentIntervalDays || invoice.organizationClient?.installmentIntervalDays || 30);
        }
        setExpirationPaymentsDtos([
          {
            ...initialExpirationPaymentDto,
            expirationDate: moment().add(30, 'days').format('YYYY-MM-DD'),
          },
        ]);
      }
    } else {
      setExpirationPaymentsDtos([{ ...initialExpirationPaymentDto }]);
      setErrorsExpirationPayment({
        expirationDate: false,
        amount: false,
        showAmountErrorInfo: false,
      });
    }
  }, [expiration, show, deliveryNote, invoice, resetExpiration, setValueExpiration]);

  useEffect(() => {
    if (!expiration) {
      updateAmountExpirationPaymentsDtos(watchExpiration('total') || 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiration, watchExpiration('total')]);

  useEffect(() => {
    if (expiration) {
      return;
    }
    const date: string | null = watchExpiration('date');
    const total: number = watchExpiration('total') || 0;
    const numPayments: number | null = watchExpiration('numPayments');
    const delayDays: number | null = watchExpiration('delayDays');
    if (date && moment(date, 'YYYY-MM-DD').isValid() && total && total !== 0 && numPayments && numPayments > 0 && delayDays && delayDays >= 0) {
      const data: CustomExpirationPaymentDto[] = [];
      const totalStr: string = total.toString();
      let isPerfectDivision: boolean;
      if (totalStr.includes('.')) {
        isPerfectDivision = Math.trunc(total * 100) % data.length === 0;
      } else {
        isPerfectDivision = total % data.length === 0;
      }
      const amount: number = parseFloat(toFixedTrunc(total / numPayments, 2));
      let accum = 0;
      for (let i = 0; i < numPayments; i++) {
        data.push({
          expirationDate:
            i === 0
              ? moment(date, 'YYYY-MM-DD').add(delayDays, 'days').format('YYYY-MM-DD')
              : moment(data[i - 1].expirationDate, 'YYYY-MM-DD')
                  .add(delayDays, 'days')
                  .format('YYYY-MM-DD'),
          amount: i > 0 && i === numPayments - 1 && !isPerfectDivision ? total - accum : amount,
          status: ExpirationPaymentStatus.Pending,
          remittanceInternalId: null,
        });
        accum += amount;
      }
      setExpirationPaymentsDtos(data);
    } else {
      setExpirationPaymentsDtos([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiration, watchExpiration('date'), watchExpiration('total'), watchExpiration('numPayments'), watchExpiration('delayDays')]);

  const updateAmountExpirationPaymentsDtos = useCallback(
    (total: number) => {
      // Repartir el total entre todos los pagos
      const data = [...expirationPaymentsDtos];
      const totalStr: string = total.toString();
      let isPerfectDivision: boolean;
      if (totalStr.includes('.')) {
        isPerfectDivision = Math.trunc(total * 100) % data.length === 0;
      } else {
        isPerfectDivision = total % data.length === 0;
      }
      const amount: number = parseFloat(toFixedTrunc(total / data.length, 2));
      let accum = 0;
      for (let i = 0; i < data.length; i++) {
        if (i > 0 && i === data.length - 1 && !isPerfectDivision) {
          data[i].amount = total - accum;
        } else {
          data[i].amount = amount;
        }
        accum += amount;
      }
      setExpirationPaymentsDtos(data);
    },
    [expirationPaymentsDtos],
  );

  const close = () => {
    resetExpiration({ ...initialExpirationDto });
    setIndexSelectedExpirationPaymentDto(-1);
    setExpirationPaymentsDtos([{ ...initialExpirationPaymentDto }]);
    setNewExpirationPayment({ ...initialExpirationPaymentDto });
    closeModal(null);
  };

  const onSubmit = async (data: CreateExpirationDto) => {
    if (expirationPaymentsDtos.length === 0) {
      myToastr.error('Debes añadir al menos un pago');
      return;
    }
    let total: number = 0;
    expirationPaymentsDtos.forEach((expirationPaymentDto) => {
      total += expirationPaymentDto.amount || 0;
    });
    if (data.total !== total) {
      myToastr.error('El total de los pagos no coincide con el total de la factura');
      return;
    }
    if (expiration) {
      try {
        const updatedExpiration = await api.updateExpiration(expiration.id, expirationPaymentsDtos);
        closeModal(updatedExpiration);
        myToastr.success('Vencimiento actualizado correctamente');
        resetExpiration({ ...initialExpirationDto });
        setExpirationPaymentsDtos([{ ...initialExpirationPaymentDto }]);
        setIndexSelectedExpirationPaymentDto(-1);
        setNewExpirationPayment({ ...initialExpirationPaymentDto });
      } catch (e: any) {
        myToastr.error(e.response.data.message);
      }
    } else {
      const createExpirationDto: CreateExpirationDto = {
        externalNumber: data.externalNumber,
        date: data.date,
        storeId: deliveryNote ? deliveryNote.storeId : data.storeId,
        deliveryNoteId: deliveryNote ? deliveryNote.id : null,
        total: data.total!,
        expirationPayments: expirationPaymentsDtos,
        invoiceId: invoice ? invoice.id : null,
      };
      try {
        const expiration = await api.createExpiration(createExpirationDto);
        closeModal(expiration);
        myToastr.success('Vencimiento creado correctamente');
        resetExpiration({ ...initialExpirationDto });
        setExpirationPaymentsDtos([{ ...initialExpirationPaymentDto }]);
        setIndexSelectedExpirationPaymentDto(-1);
        setNewExpirationPayment({ ...initialExpirationPaymentDto });
      } catch (e: any) {
        myToastr.error(e.response.data.message);
      }
    }
  };

  const addExpirationPaymentDto = () => {
    let hasErrors: boolean = false;
    if (!newExpirationPayment.expirationDate || !moment(newExpirationPayment.expirationDate).isValid()) {
      setErrorsExpirationPayment({ ...errorsExpirationPayment, expirationDate: true });
      hasErrors = true;
    }
    if (newExpirationPayment.amount === null || newExpirationPayment.amount === 0) {
      setErrorsExpirationPayment({ ...errorsExpirationPayment, amount: true });
      hasErrors = true;
    }
    if (hasErrors) {
      return;
    }
    const epmDtos = [...expirationPaymentsDtos];
    epmDtos.push(newExpirationPayment);
    setExpirationPaymentsDtos(epmDtos);
    setNewExpirationPayment({ ...initialExpirationPaymentDto });
    setErrorsExpirationPayment({ expirationDate: false, amount: false, showAmountErrorInfo: false });
  };

  const cancelExpirationPaymentDto = () => {
    setIndexSelectedExpirationPaymentDto(-1);
    setNewExpirationPayment({ ...initialExpirationPaymentDto });
    setErrorsExpirationPayment({ expirationDate: false, amount: false, showAmountErrorInfo: false });
  };

  const updateExpirationPaymentDto = () => {
    let hasErrors: boolean = false;
    if (!newExpirationPayment.expirationDate || !moment(newExpirationPayment.expirationDate).isValid()) {
      setErrorsExpirationPayment({ ...errorsExpirationPayment, expirationDate: true });
      hasErrors = true;
    }
    if (newExpirationPayment.amount === null || newExpirationPayment.amount === 0) {
      setErrorsExpirationPayment({ ...errorsExpirationPayment, amount: true });
      hasErrors = true;
    }
    if (hasErrors) {
      return;
    }
    const epmDtos = [...expirationPaymentsDtos];
    epmDtos[indexSelectedExpirationPaymentDto] = newExpirationPayment;
    setExpirationPaymentsDtos(epmDtos);
    setNewExpirationPayment({ ...initialExpirationPaymentDto });
    setErrorsExpirationPayment({ expirationDate: false, amount: false, showAmountErrorInfo: false });
    setIndexSelectedExpirationPaymentDto(-1);
  };

  const deleteExpirationPaymentDto = (index: number) => {
    const epmDtos = [...expirationPaymentsDtos];
    epmDtos.splice(index, 1);
    setExpirationPaymentsDtos(epmDtos);
    setValueExpiration('numPayments', epmDtos.length);
  };

  const hasTotal = getValuesExpiration('total') != null && getValuesExpiration('total')! !== 0;
  const isExternalNumberInputDisabled = Boolean(deliveryNote) || Boolean(invoice) || indexSelectedExpirationPaymentDto !== -1;
  const isDateInputDisabled = Boolean(expiration) || indexSelectedExpirationPaymentDto !== -1;
  const isTotalInputDisabled = Boolean(expiration) || indexSelectedExpirationPaymentDto !== -1;
  const isNumPaymentsInputDisabled = Boolean(expiration) || indexSelectedExpirationPaymentDto !== -1;
  const isDelayDaysInputDisabled = Boolean(expiration) || indexSelectedExpirationPaymentDto !== -1;
  const someExpirationPaymentIsLinkedToRemittance = Boolean(expiration?.expirationPayments.some((ep) => ep.remittanceId));

  return (
    <Modal className="vercomi-modal my-form expiration-modal" isOpen={show} onRequestClose={close} shouldCloseOnOverlayClick={false}>
      <form onSubmit={handleSubmitExpiration(onSubmit)}>
        <div className="content">
          <div className="d-flex justify-content-between mb-3">
            <div className="title">{expiration ? 'Actualizar vencimiento' : 'Nuevo vencimiento'}</div>
            <button type="button" className="close-button-modal" onClick={close} title="Cerrar">
              <X size={16} />
            </button>
          </div>
          <div className="row my-3">
            <div className="col">
              <div className={clsx('input-name', { error: errorsExpiration?.externalNumber })}>Número de referencia o factura *</div>
              <input
                type="text"
                {...registerExpiration('externalNumber', { required: true })}
                className={clsx({ error: errorsExpiration?.externalNumber })}
                disabled={isExternalNumberInputDisabled}
                placeholder="Número de referencia o factura"
              />
              {errorsExpiration.externalNumber && <div className="error-message">{errorsExpiration.externalNumber.message}</div>}
            </div>
            <div className="col">
              <div className={clsx('input-name', { error: errorsExpiration?.date })}>Fecha de inicio del vencimiento *</div>
              <input type="date" {...registerExpiration('date', { required: true })} className={clsx({ error: errorsExpiration?.date })} placeholder="Fecha" disabled={isDateInputDisabled} />
              {errorsExpiration.date && <div className="error-message">{errorsExpiration.date.message}</div>}
            </div>
          </div>
          <div className="row my-3">
            <div className="col-6">
              <div className={clsx('input-name', { error: errorsExpiration?.total })}>Total a pagar *</div>
              <input
                {...registerExpiration('total', { required: true, valueAsNumber: true, validate: (value: number | null) => (value !== null && value !== 0 ? true : 'El total no puede ser 0') })}
                className={clsx({ error: errorsExpiration?.total })}
                disabled={isTotalInputDisabled}
                placeholder="Total"
                step="0.01"
                type="number"
              />
              {errorsExpiration.total && <div className="error-message">{errorsExpiration.total.message}</div>}
            </div>
            <div className="col-6">
              <div className={clsx('input-name', { error: errorsExpiration?.numPayments })}>Número de pagos *</div>
              <input
                type="number"
                {...registerExpiration('numPayments', {
                  min: 0,
                  required: true,
                  validate: (value: number | null) => (value !== null && value >= 0 ? true : 'El número de pagos no puede ser 0'),
                  valueAsNumber: true,
                })}
                className={clsx({ error: errorsExpiration?.numPayments })}
                disabled={isNumPaymentsInputDisabled}
                placeholder="Número de pagos"
                step="1"
              />
              {errorsExpiration.numPayments && <div className="error-message">{errorsExpiration.numPayments.message}</div>}
            </div>
            <div className="col-6 mt-3">
              <div className={clsx('input-name', { error: errorsExpiration?.delayDays })}>Carencia en días *</div>
              <input
                type="number"
                {...registerExpiration('delayDays', {
                  min: 0,
                  required: true,
                  validate: (value: number | null) => (value !== null && value >= 0 ? true : 'La carencia no puede ser menor que 0'),
                  valueAsNumber: true,
                })}
                className={clsx({ error: errorsExpiration?.delayDays })}
                disabled={isDelayDaysInputDisabled}
                placeholder="Carencia de pagos"
                step="1"
              />
              {errorsExpiration.delayDays && <div className="error-message">{errorsExpiration.delayDays.message}</div>}
            </div>
          </div>
          {someExpirationPaymentIsLinkedToRemittance && (
            <div className="my-2">
              <strong style={{ fontSize: 12, color: 'orange' }}>No se permiten modificaciones porque hay pagos vinculados a remesas</strong>
            </div>
          )}
          <h5>Listado de pagos</h5>
          <div className="table-payments">
            <table className="my-table">
              <thead>
                <tr>
                  <th>Fecha vencimiento pago</th>
                  <th>Importe</th>
                  <th>Estado</th>
                  <th>Remesa</th>
                  {!someExpirationPaymentIsLinkedToRemittance && <th>Acción</th>}
                </tr>
              </thead>
              <tbody>
                {!hasTotal ? (
                  <tr>
                    <td colSpan={4} className="text-center">
                      Introduce el total de la factura para añadir pagos
                    </td>
                  </tr>
                ) : expirationPaymentsDtos.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center">
                      No hay pagos
                    </td>
                  </tr>
                ) : (
                  expirationPaymentsDtos.map((expirationPaymentDto, index: number) => {
                    const disableEdit = indexSelectedExpirationPaymentDto !== -1 || expirationPaymentDto.status === ExpirationPaymentStatus.Paid;
                    const disableDelete = indexSelectedExpirationPaymentDto !== -1 || expirationPaymentDto.status === ExpirationPaymentStatus.Paid;
                    return (
                      <tr key={index}>
                        <td>{moment(expirationPaymentDto.expirationDate).format('DD/MM/YYYY')}</td>
                        <td>{formatNumber(expirationPaymentDto.amount!)}€</td>
                        <td>{expirationPaymentStatusToString(expirationPaymentDto.status)}</td>
                        <td>{expirationPaymentDto.remittanceInternalId}</td>
                        {!someExpirationPaymentIsLinkedToRemittance && (
                          <td className="align-middle text-center">
                            <div className="d-flex flex-row align-items-center gap-2">
                              <Edit
                                size={16}
                                color="#222E3D"
                                className={clsx(disableEdit && 'cursor-not-allowed')}
                                onClick={() => {
                                  if (disableEdit) {
                                    return;
                                  }
                                  setIndexSelectedExpirationPaymentDto(index);
                                  setNewExpirationPayment({ ...expirationPaymentDto });
                                }}
                                type="button"
                              />
                              {expirationPaymentDto.status === ExpirationPaymentStatus.Pending && (
                                <Trash2
                                  className={clsx(disableDelete && 'cursor-not-allowed')}
                                  onClick={() => {
                                    if (disableDelete) {
                                      return;
                                    }
                                    deleteExpirationPaymentDto(index);
                                  }}
                                  size={14}
                                  type="button"
                                />
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {hasTotal && indexSelectedExpirationPaymentDto > -1 && (
            <div className="flex flex-column">
              <h6>Editar pago vencimiento:</h6>
              <div className="d-flex justify-content-between align-items-end mt-3">
                <div className="">
                  <div className={clsx('input-name', { error: errorsExpirationPayment.expirationDate })}>Fecha vencimiento *</div>
                  <input
                    type="date"
                    placeholder="Fecha vencimiento"
                    value={newExpirationPayment.expirationDate}
                    onChange={(e: any) => {
                      setNewExpirationPayment({ ...newExpirationPayment, expirationDate: e.target.value });
                    }}
                    className={clsx({ error: errorsExpirationPayment.expirationDate })}
                  />
                </div>
                <div
                  className=""
                  onClick={() => {
                    if (indexSelectedExpirationPaymentDto > -1 && newExpirationPayment.status === ExpirationPaymentStatus.Paid) {
                      setErrorsExpirationPayment({ ...errorsExpirationPayment, showAmountErrorInfo: true });
                    }
                  }}
                >
                  <div className={clsx('input-name', { error: errorsExpirationPayment.amount })}>Importe *</div>
                  <input
                    disabled={indexSelectedExpirationPaymentDto > -1 && newExpirationPayment.status === ExpirationPaymentStatus.Paid}
                    value={newExpirationPayment.amount !== null ? newExpirationPayment.amount : ''}
                    className={clsx({ error: errorsExpirationPayment.amount })}
                    onChange={(e: any) => {
                      setErrorsExpirationPayment({ ...errorsExpirationPayment, amount: false });
                      if (e.target.value) {
                        setNewExpirationPayment({ ...newExpirationPayment, amount: parseFloat(e.target.value) });
                      } else {
                        setNewExpirationPayment({ ...newExpirationPayment, amount: null });
                      }
                    }}
                    type="number"
                    placeholder="Importe"
                    step="0.01"
                  />
                </div>
                <div className="d-flex flex-row">
                  {indexSelectedExpirationPaymentDto > -1 ? (
                    <React.Fragment>
                      <button type="button" onClick={updateExpirationPaymentDto} className={clsx('button-expiration-payment add-expiration-payment')}>
                        Actualizar
                      </button>
                      <button onClick={cancelExpirationPaymentDto} type="button" className={clsx('button-expiration-payment cancel-expiration-payment')}>
                        Cancelar
                      </button>
                    </React.Fragment>
                  ) : (
                    <button type="button" onClick={addExpirationPaymentDto} className={clsx('button-expiration-payment add-expiration-payment', { disabled: !validNewExpirationPayment })}>
                      Añadir pago
                    </button>
                  )}
                </div>
              </div>
              {errorsExpirationPayment.showAmountErrorInfo && <div className="error-message mt-3">No se puede modificar el importe de un pago en estado Pagado</div>}
            </div>
          )}
        </div>
        <button className="save-button" disabled={indexSelectedExpirationPaymentDto !== -1} type="submit">
          {someExpirationPaymentIsLinkedToRemittance ? 'Cerrar' : 'Guardar'}
        </button>
      </form>
    </Modal>
  );
};

export default ExpirationModal;
